import React from 'react';
import { useAgencyMutation, useAgencyResource } from './storeAgencyHooks';

const shareState = value => ({
  pending: 'En cola para WhatsApp',
  sending: 'Enviando por WhatsApp',
  accepted: 'WhatsApp aceptó el mensaje',
  failed: 'WhatsApp rechazó el envío',
  uncertain: 'Envío incierto · requiere revisión',
  skipped: 'No enviado por la configuración actual',
}[value] || 'Todavía no enviado por Fast Max');

function absoluteUrl(value) {
  if (!value) return '';
  try { return new URL(value, window.location.origin).toString(); }
  catch (_) { return ''; }
}

function whatsappUrl(phone, message) {
  let digits = String(phone || '').replace(/\D/g, '');
  if (digits.length === 9) digits = `51${digits}`;
  return digits ? `https://wa.me/${digits}?text=${encodeURIComponent(message)}` : '';
}

export default function StoreAgencyShare({ id, row, state, caps, closed, onChanged }) {
  const resource = useAgencyResource(state && !state.revoked && !closed ? `/payment/shipments/${id}/link` : null);
  const mutation = useAgencyMutation();
  const link = absoluteUrl(resource.data?.url);
  const message = link
    ? `Hola ${row.recipient_name}, sigue tu envío ${row.reference} de Fast Max y coordina el pago o recojo aquí: ${link}\n\nEste enlace es privado. No lo compartas.`
    : '';
  const nativeUrl = whatsappUrl(row.recipient_phone, message);
  const status = state.share_status;
  const processing = ['pending', 'sending'].includes(status?.state);
  const resend = ['accepted', 'failed', 'uncertain', 'skipped'].includes(status?.state);
  const unavailable = mutation.busy || state.revoked || closed;
  const fastMaxLocked = unavailable || !caps.writes_enabled;

  async function record(action) {
    await mutation.run(async write => {
      await write(`/payment/shipments/${id}/share`, { action, version: state.version });
    }, action === 'copied' ? 'Enlace copiado.' : 'WhatsApp de tu tienda abierto.');
  }

  async function sendFromFastMax() {
    await mutation.run(async write => {
      await write(`/payment/shipments/${id}/share`, {
        action: 'send_whatsapp', version: state.version, ...(resend ? { confirm_resend: true } : {}),
      });
      onChanged();
    }, resend ? 'Reenvío solicitado a Fast Max.' : 'Envío solicitado a Fast Max.');
  }

  return <section className="sa-share" aria-labelledby={`agency-share-${id}`}>
    <div className="sa-share-heading">
      <div><span className="sa-share-kicker">Enlace para tu cliente</span><h3 id={`agency-share-${id}`}>Seguimiento privado listo para compartir</h3></div>
      <span className="sa-share-security">Acceso protegido</span>
    </div>
    <p>El cliente verá el avance, el comprobante publicado, el QR autorizado y la clave solo cuando corresponda.</p>
    {resource.loading && <p role="status">Construyendo enlace privado…</p>}
    {resource.error && <p role="alert" className="sa-alert">{resource.error} <button type="button" onClick={resource.refresh}>Reintentar</button></p>}
    {link && <>
      <label className="sa-share-link">Enlace único del envío<input readOnly value={link} onFocus={event => event.currentTarget.select()} /></label>
      <div className="sa-share-actions">
        <button className="sa-primary" type="button" disabled={unavailable || !link || !navigator.clipboard} onClick={() => mutation.run(async write => {
          await navigator.clipboard.writeText(link);
          if (caps.writes_enabled) await write(`/payment/shipments/${id}/share`, { action: 'copied', version: state.version });
        }, 'Enlace copiado.')}>Copiar enlace</button>
        <a className={`sa-button sa-whatsapp${!nativeUrl ? ' is-disabled' : ''}`} href={nativeUrl || undefined}
          target="_blank" rel="noopener noreferrer" aria-disabled={!nativeUrl}
          onClick={event => { if (!nativeUrl) { event.preventDefault(); return; } if (caps.writes_enabled) record('native_whatsapp_opened'); }}>
          Compartir desde mi WhatsApp
        </a>
        <a className="sa-button" href={link} target="_blank" rel="noopener noreferrer">Ver página del cliente</a>
      </div>
      <div className="sa-share-fastmax">
        <div><strong>Enviar desde Fast Max Business</strong><p>{shareState(status?.state)}. Los siguientes avances se enviarán según la ventana de 24 horas o una plantilla aprobada.</p></div>
        <button type="button" disabled={fastMaxLocked || processing || !caps.whatsapp_enabled || !state.whatsapp_consent}
          onClick={sendFromFastMax}>{processing ? 'Envío en proceso' : resend ? 'Reenviar desde Fast Max' : 'Enviar desde Fast Max'}</button>
      </div>
      {!state.whatsapp_consent && <p className="sa-share-note">No se registró autorización para mensajes automáticos. Aún puedes copiar el enlace o compartirlo desde tu propio WhatsApp.</p>}
      {state.whatsapp_consent && !caps.whatsapp_enabled && <p className="sa-share-note">El canal automático está pausado por Fast Max. Compartir desde tu WhatsApp sigue disponible.</p>}
    </>}
    {mutation.error && <p role="alert" className="sa-alert">{mutation.error}</p>}
    {mutation.notice && <p role="status" className="sa-success">{mutation.notice}</p>}
    <p className="sa-share-warning">No publiques este enlace en redes ni lo envíes a otra persona: permite ver información privada del envío.</p>
  </section>;
}
